import { useCallback } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import {
  Block,
  HeadingXSmall,
  LabelMedium,
  Lock03,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { setOpenModal, useDappDispatch } from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import TooltipLabel from "#/shared/components/TooltipLabel"
import { DepositMigrationStatus } from "#/shared/types"
import { getTokenIcon } from "#/shared/utils"

import { TokenLabel } from "../Token"

import DepositReturnAction from "./DepositReturnAction"

type DepositBoxInfoProps = {
  token: SupportedTokenName
  userAmount: string
  unlockTime: string | null
  depositId: number | null
  id: string
  onWithdrawalButtonClick: () => void
  onReturnButtonClick: () => void
  mintedAmount: bigint
  migrationStatus?: DepositMigrationStatus
}

export default function DepositBoxInfo({
  token,
  userAmount,
  unlockTime,
  onWithdrawalButtonClick,
  onReturnButtonClick,
  depositId,
  id,
  mintedAmount,
  migrationStatus = DepositMigrationStatus.NotEligible,
}: DepositBoxInfoProps) {
  const dispatch = useDappDispatch()
  const [css, theme] = useStyletron()

  const tokenIcon = getTokenIcon(token)
  const canReturnAsset = !unlockTime && !!depositId
  const isBeingMigrated = migrationStatus === DepositMigrationStatus.InProgress

  const onMigrateClick = useCallback(async () => {
    dispatch(setOpenModal("migrate-deposit", { initialActiveDepositUuid: id }))
  }, [id, dispatch])

  if (isBeingMigrated) {
    return (
      <Block
        backgroundColor={theme.colors.white}
        padding={`${theme.sizing.scale900} ${theme.sizing.scale600}`}
      >
        <TooltipLabel value="Estimated completion in 7-14 days">
          <HeadingXSmall padding={`${theme.sizing.scale0} 0`}>
            {userAmount} <CurrencySymbol token={token} /> →{" "}
            <CurrencySymbol token="tBTC" /> migration in progress
          </HeadingXSmall>
        </TooltipLabel>
      </Block>
    )
  }

  return (
    <Block
      display="flex"
      backgroundColor={theme.colors.white}
      justifyContent="space-between"
      alignItems="center"
      padding={`${theme.sizing.scale600} ${theme.sizing.scale800} ${theme.sizing.scale600} 0`}
      className={css({ gap: theme.sizing.scale500 })}
    >
      <Block>
        <TokenLabel
          token={token}
          tokenIcon={tokenIcon}
          amount={userAmount}
          showDepositMigrationButton={false}
          onClick={onMigrateClick}
          showSuperstackIcon={mintedAmount > 0}
        />
      </Block>
      <Block display="flex" justifyContent="flex-end">
        {unlockTime && (
          <Block>
            <LabelMedium
              className={css({
                display: "inline-flex",
                alignItems: "center",
                gap: theme.sizing.scale100,
                justifyContent: "flex-end",
                width: "100%",
              })}
            >
              <Lock03 size={16} />
              Locked
            </LabelMedium>
            <ParagraphSmall color="contentSecondary">
              {unlockTime}
            </ParagraphSmall>
          </Block>
        )}
        {canReturnAsset && (
          <DepositReturnAction
            token={token}
            userAmount={userAmount}
            mintedAmount={mintedAmount}
            onReturnButtonClick={onReturnButtonClick}
            onWithdrawalButtonClick={onWithdrawalButtonClick}
          />
        )}
      </Block>
    </Block>
  )
}
